import { ArrowDownOutlined, ArrowUpOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import type { DocFieldSpec, DocumentSource, LayoutBlock, LayoutBlockType, LayoutFieldRow, LayoutFormat, LayoutTableColumn, LayoutTableTotal, TemplateLayout } from "@wms/domain";
import { Button, Checkbox, Col, Input, InputNumber, Row, Select, Space, Switch, Tag, Tooltip, Typography } from "antd";
import { useState } from "react";
import type { ReactNode } from "react";
import {
  BLOCK_LABELS,
  BLOCK_ORDER,
  DEFAULT_PAGE,
  FORMAT_OPTIONS,
  blockSummary,
  cannotAdd,
  defaultBlock,
  fieldOptions,
  moveItem,
  removeAt,
  replaceAt,
  tableColumns,
  tableNames,
  withPlaceholder,
} from "../pages/templateLayout";

interface Props {
  value: TemplateLayout;
  onChange: (layout: TemplateLayout) => void;
  source: DocumentSource | null;
  readOnly: boolean;
}

function Labeled({ label, children, span = 12 }: { label: string; children: ReactNode; span?: number }) {
  return (
    <Col xs={24} md={span}>
      {/* a div, not a <label>: a label forwards clicks to its first input and steals focus from the field picker */}
      <div style={{ marginBottom: 8 }}>
        <span style={{ display: "block", fontSize: 12, color: "var(--wms-muted)", marginBottom: 2 }}>{label}</span>
        {children}
      </div>
    </Col>
  );
}

function FieldSelect({ fields, value, onChange, disabled, placeholder, testid, allowClear }: { fields: DocFieldSpec[]; value?: string | null; onChange: (v: string | null) => void; disabled: boolean; placeholder?: string; testid?: string; allowClear?: boolean }) {
  const options = fieldOptions(fields);
  const known = !value || options.some((o) => o.value === value);
  return (
    <Select
      showSearch
      allowClear={allowClear}
      value={value ?? undefined}
      onChange={(v) => onChange(v ?? null)}
      disabled={disabled}
      placeholder={placeholder ?? "Выберите поле"}
      options={options}
      filterOption={(input, option) => (option?.search ?? "").includes(input.toLowerCase())}
      style={{ width: "100%" }}
      status={known ? undefined : "error"}
      data-testid={testid}
      popupMatchSelectWidth={false}
    />
  );
}

function IconButtons({ index, count, onMove, onRemove, readOnly, testid }: { index: number; count: number; onMove: (to: number) => void; onRemove: () => void; readOnly: boolean; testid: string }) {
  if (readOnly) return null;
  return (
    <Space size={2}>
      <Tooltip title="Выше">
        <Button size="small" type="text" icon={<ArrowUpOutlined />} disabled={index === 0} onClick={() => onMove(index - 1)} aria-label="Выше" data-testid={`${testid}-up`} />
      </Tooltip>
      <Tooltip title="Ниже">
        <Button size="small" type="text" icon={<ArrowDownOutlined />} disabled={index === count - 1} onClick={() => onMove(index + 1)} aria-label="Ниже" data-testid={`${testid}-down`} />
      </Tooltip>
      <Tooltip title="Удалить">
        <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={onRemove} aria-label="Удалить" data-testid={`${testid}-remove`} />
      </Tooltip>
    </Space>
  );
}

/** Rows «подпись — поле — формат — жирный» of the blocks «Реквизиты», «Итоги» and a column of «Стороны». */
function RowsEditor({ rows, onChange, fields, readOnly, testid }: { rows: LayoutFieldRow[]; onChange: (rows: LayoutFieldRow[]) => void; fields: DocFieldSpec[]; readOnly: boolean; testid: string }) {
  return (
    <div>
      {rows.map((row, i) => (
        <div key={i} className="wms-layout-row" data-testid={`${testid}-row-${i}`}>
          <Input value={row.label} disabled={readOnly} maxLength={120} placeholder="Подпись" onChange={(e) => onChange(replaceAt(rows, i, { ...row, label: e.target.value }))} />
          <FieldSelect fields={fields} value={row.field} disabled={readOnly} onChange={(v) => onChange(replaceAt(rows, i, { ...row, field: v ?? row.field }))} testid={`${testid}-row-${i}-field`} />
          <Select
            allowClear
            placeholder="Формат поля"
            value={row.format ?? undefined}
            disabled={readOnly}
            options={FORMAT_OPTIONS}
            onChange={(v) => onChange(replaceAt(rows, i, { ...row, format: (v as LayoutFormat | undefined) ?? null }))}
          />
          <Space size={4}>
            <Checkbox checked={!!row.bold} disabled={readOnly} onChange={(e) => onChange(replaceAt(rows, i, { ...row, bold: e.target.checked }))}>
              жирный
            </Checkbox>
            <IconButtons index={i} count={rows.length} readOnly={readOnly} onMove={(to) => onChange(moveItem(rows, i, to))} onRemove={() => onChange(removeAt(rows, i))} testid={`${testid}-row-${i}`} />
          </Space>
        </div>
      ))}
      {!readOnly && (
        <Button size="small" icon={<PlusOutlined />} onClick={() => onChange([...rows, { label: "Поле", field: fields[0]?.path ?? "document.number" }])} data-testid={`${testid}-add-row`}>
          Строка
        </Button>
      )}
    </div>
  );
}

function TextWithFields({ value, onChange, fields, readOnly, multiline, maxLength, placeholder, testid }: { value: string; onChange: (v: string) => void; fields: DocFieldSpec[]; readOnly: boolean; multiline?: boolean; maxLength: number; placeholder?: string; testid?: string }) {
  // the picker inserts `{path}` and starts empty again (a new key remounts it with its placeholder)
  const [pickKey, setPickKey] = useState(0);
  return (
    <Space.Compact style={{ width: "100%" }} direction={multiline ? "vertical" : "horizontal"}>
      {multiline ? (
        <Input.TextArea value={value} disabled={readOnly} maxLength={maxLength} autoSize={{ minRows: 2, maxRows: 6 }} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid} />
      ) : (
        <Input value={value} disabled={readOnly} maxLength={maxLength} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid} />
      )}
      {!readOnly && (
        <Select
          key={pickKey}
          showSearch
          placeholder="+ поле"
          options={fieldOptions(fields)}
          filterOption={(input, option) => (option?.search ?? "").includes(input.toLowerCase())}
          onChange={(path: string) => {
            onChange(withPlaceholder(value, path));
            setPickKey((k) => k + 1);
          }}
          popupMatchSelectWidth={false}
          style={{ minWidth: 110 }}
          aria-label="Вставить поле"
        />
      )}
    </Space.Compact>
  );
}

function BlockBody({ block, onChange, source, readOnly, testid }: { block: LayoutBlock; onChange: (b: LayoutBlock) => void; source: DocumentSource | null; readOnly: boolean; testid: string }) {
  const fields = source?.fields ?? [];
  switch (block.type) {
    case "title":
      return (
        <Row gutter={12}>
          <Labeled label="Текст заголовка (можно вставить поле: {document.number})">
            <TextWithFields value={block.text} onChange={(text) => onChange({ ...block, text })} fields={fields} readOnly={readOnly} maxLength={200} testid={`${testid}-text`} />
          </Labeled>
          <Labeled label="Подзаголовок">
            <TextWithFields value={block.subtitle ?? ""} onChange={(subtitle) => onChange({ ...block, subtitle: subtitle || null })} fields={fields} readOnly={readOnly} maxLength={300} />
          </Labeled>
          <Labeled label="Номер и дата под заголовком" span={8}>
            <Switch checked={block.number !== false} disabled={readOnly} onChange={(number) => onChange({ ...block, number })} />
          </Labeled>
          <Labeled label="Выравнивание" span={8}>
            <Select value={block.align ?? "center"} disabled={readOnly} onChange={(align) => onChange({ ...block, align })} options={[{ value: "center", label: "По центру" }, { value: "left", label: "Слева" }]} style={{ width: "100%" }} />
          </Labeled>
        </Row>
      );
    case "text":
      return (
        <Row gutter={12}>
          <Labeled label="Текст (поля в фигурных скобках подставятся из документа)" span={16}>
            <TextWithFields value={block.text} onChange={(text) => onChange({ ...block, text })} fields={fields} readOnly={readOnly} multiline maxLength={2000} testid={`${testid}-text`} />
          </Labeled>
          <Labeled label="Стиль" span={8}>
            <Select
              value={block.style ?? "normal"}
              disabled={readOnly}
              onChange={(style) => onChange({ ...block, style })}
              options={[{ value: "normal", label: "Обычный" }, { value: "small", label: "Мелкий" }, { value: "bold", label: "Жирный" }, { value: "note", label: "Примечание" }]}
              style={{ width: "100%" }}
            />
          </Labeled>
        </Row>
      );
    case "fields":
      return (
        <>
          <Row gutter={12}>
            <Labeled label="Заголовок блока" span={12}>
              <Input value={block.title ?? ""} disabled={readOnly} maxLength={120} onChange={(e) => onChange({ ...block, title: e.target.value || null })} />
            </Labeled>
            <Labeled label="Колонок" span={6}>
              <Select value={block.columns ?? 1} disabled={readOnly} onChange={(columns) => onChange({ ...block, columns })} options={[{ value: 1, label: "1" }, { value: 2, label: "2" }]} style={{ width: "100%" }} />
            </Labeled>
            <Labeled label="Скрывать пустые" span={6}>
              <Switch checked={!!block.hide_empty} disabled={readOnly} onChange={(hide_empty) => onChange({ ...block, hide_empty })} />
            </Labeled>
          </Row>
          <RowsEditor rows={block.rows} onChange={(rows) => onChange({ ...block, rows })} fields={fields} readOnly={readOnly} testid={testid} />
        </>
      );
    case "totals":
      return <RowsEditor rows={block.rows} onChange={(rows) => onChange({ ...block, rows })} fields={fields} readOnly={readOnly} testid={testid} />;
    case "parties":
      return (
        <Space direction="vertical" style={{ width: "100%" }}>
          {block.columns.map((column, ci) => (
            <div key={ci} style={{ borderLeft: "3px solid var(--wms-border)", paddingLeft: 10 }}>
              <div className="wms-layout-block-head" style={{ marginBottom: 4 }}>
                <Input
                  value={column.title}
                  disabled={readOnly}
                  maxLength={120}
                  style={{ maxWidth: 360 }}
                  onChange={(e) => onChange({ ...block, columns: replaceAt(block.columns, ci, { ...column, title: e.target.value }) })}
                  placeholder="Заголовок стороны"
                />
                <IconButtons index={ci} count={block.columns.length} readOnly={readOnly || block.columns.length === 1} onMove={(to) => onChange({ ...block, columns: moveItem(block.columns, ci, to) })} onRemove={() => onChange({ ...block, columns: removeAt(block.columns, ci) })} testid={`${testid}-side-${ci}`} />
              </div>
              <RowsEditor rows={column.rows} onChange={(rows) => onChange({ ...block, columns: replaceAt(block.columns, ci, { ...column, rows }) })} fields={fields} readOnly={readOnly} testid={`${testid}-side-${ci}`} />
            </div>
          ))}
          {!readOnly && block.columns.length < 3 && (
            <Button size="small" icon={<PlusOutlined />} onClick={() => onChange({ ...block, columns: [...block.columns, { title: "Сторона", rows: [{ label: "Наименование", field: fields[0]?.path ?? "document.number" }] }] })}>
              Сторона
            </Button>
          )}
        </Space>
      );
    case "table":
      return <TableBody block={block} onChange={onChange} source={source} readOnly={readOnly} testid={testid} />;
    case "signatures":
      return (
        <>
          <Row gutter={12}>
            <Labeled label="Колонок" span={8}>
              <Select value={block.columns ?? 2} disabled={readOnly} onChange={(columns) => onChange({ ...block, columns })} options={[1, 2, 3].map((v) => ({ value: v, label: String(v) }))} style={{ width: "100%" }} />
            </Labeled>
            <Labeled label="«М. П.» рядом с подписями" span={8}>
              <Switch checked={!!block.stamp} disabled={readOnly} onChange={(stamp) => onChange({ ...block, stamp })} />
            </Labeled>
          </Row>
          {block.items.map((item, i) => (
            <div key={i} className="wms-layout-row">
              <Input value={item.role} disabled={readOnly} maxLength={80} placeholder="Должность / роль" onChange={(e) => onChange({ ...block, items: replaceAt(block.items, i, { ...item, role: e.target.value }) })} />
              <FieldSelect fields={fields} value={item.name_field ?? null} allowClear placeholder="ФИО из поля (необязательно)" disabled={readOnly} onChange={(v) => onChange({ ...block, items: replaceAt(block.items, i, { ...item, name_field: v }) })} />
              <span />
              <IconButtons index={i} count={block.items.length} readOnly={readOnly} onMove={(to) => onChange({ ...block, items: moveItem(block.items, i, to) })} onRemove={() => onChange({ ...block, items: removeAt(block.items, i) })} testid={`${testid}-sign-${i}`} />
            </div>
          ))}
          {!readOnly && block.items.length < 8 && (
            <Button size="small" icon={<PlusOutlined />} onClick={() => onChange({ ...block, items: [...block.items, { role: "Подпись" }] })}>
              Подпись
            </Button>
          )}
        </>
      );
    case "barcode":
      return (
        <Row gutter={12}>
          <Labeled label="Значение из поля">
            <FieldSelect fields={fields} value={block.field} disabled={readOnly} onChange={(v) => onChange({ ...block, field: v ?? block.field })} />
          </Labeled>
          <Labeled label="Вид" span={6}>
            <Select value={block.kind ?? "code128"} disabled={readOnly} onChange={(kind) => onChange({ ...block, kind })} options={[{ value: "code128", label: "Code 128" }, { value: "qr", label: "QR-код" }]} style={{ width: "100%" }} />
          </Labeled>
          <Labeled label="Высота, мм" span={6}>
            <InputNumber min={6} max={40} value={block.height_mm ?? 12} disabled={readOnly} onChange={(v) => onChange({ ...block, height_mm: Number(v ?? 12) })} style={{ width: "100%" }} />
          </Labeled>
        </Row>
      );
    case "spacer":
      return (
        <Row gutter={12}>
          <Labeled label="Высота, мм" span={8}>
            <InputNumber min={1} max={60} value={block.height_mm ?? 4} disabled={readOnly} onChange={(v) => onChange({ ...block, height_mm: Number(v ?? 4) })} style={{ width: "100%" }} />
          </Labeled>
        </Row>
      );
    case "page_break":
      return <Typography.Text type="secondary">Следующий блок начнётся с новой страницы.</Typography.Text>;
  }
}

function TableBody({ block, onChange, source, readOnly, testid }: { block: Extract<LayoutBlock, { type: "table" }>; onChange: (b: LayoutBlock) => void; source: DocumentSource | null; readOnly: boolean; testid: string }) {
  const columnsOfSource = tableColumns(source, block.source);
  const headerFields = source?.fields ?? [];
  const totals = block.totals ?? [];
  const setColumns = (columns: LayoutTableColumn[]) => onChange({ ...block, columns });
  const setTotals = (next: LayoutTableTotal[]) => onChange({ ...block, totals: next });
  return (
    <>
      <Row gutter={12}>
        <Labeled label="Строки из" span={8}>
          <Select
            value={block.source}
            disabled={readOnly}
            options={tableNames(source).map((t) => ({ value: t, label: t }))}
            onChange={(name: string) => {
              const cols = tableColumns(source, name).slice(0, 4);
              onChange({ ...block, source: name, columns: cols.map((c) => ({ title: c.label, field: c.path, width: 10 })), totals: [] });
            }}
            style={{ width: "100%" }}
          />
        </Labeled>
        <Labeled label="Заголовок таблицы" span={8}>
          <Input value={block.title ?? ""} disabled={readOnly} maxLength={120} onChange={(e) => onChange({ ...block, title: e.target.value || null })} />
        </Labeled>
        <Labeled label="Колонка «№»" span={8}>
          <Switch checked={!!block.numbered} disabled={readOnly} onChange={(numbered) => onChange({ ...block, numbered })} />
        </Labeled>
      </Row>
      <Typography.Text strong style={{ display: "block", margin: "4px 0" }}>
        Колонки
      </Typography.Text>
      {block.columns.map((column, i) => (
        <div key={i} className="wms-layout-row" data-testid={`${testid}-col-${i}`}>
          <Input value={column.title} disabled={readOnly} maxLength={80} placeholder="Заголовок колонки" onChange={(e) => setColumns(replaceAt(block.columns, i, { ...column, title: e.target.value }))} />
          <FieldSelect fields={columnsOfSource} value={column.field} disabled={readOnly} onChange={(v) => setColumns(replaceAt(block.columns, i, { ...column, field: v ?? column.field }))} />
          <Space.Compact style={{ width: "100%" }}>
            <InputNumber min={1} max={60} value={column.width ?? 10} disabled={readOnly} onChange={(v) => setColumns(replaceAt(block.columns, i, { ...column, width: Number(v ?? 10) }))} style={{ width: "50%" }} aria-label="Ширина (доля)" />
            <Select
              allowClear
              placeholder="Формат"
              value={column.format ?? undefined}
              disabled={readOnly}
              options={FORMAT_OPTIONS}
              onChange={(v) => setColumns(replaceAt(block.columns, i, { ...column, format: (v as LayoutFormat | undefined) ?? null }))}
              style={{ width: "50%" }}
            />
          </Space.Compact>
          <IconButtons index={i} count={block.columns.length} readOnly={readOnly} onMove={(to) => setColumns(moveItem(block.columns, i, to))} onRemove={() => block.columns.length > 1 && setColumns(removeAt(block.columns, i))} testid={`${testid}-col-${i}`} />
        </div>
      ))}
      {!readOnly && block.columns.length < 16 && (
        <Button size="small" icon={<PlusOutlined />} onClick={() => setColumns([...block.columns, { title: columnsOfSource[0]?.label ?? "Колонка", field: columnsOfSource[0]?.path ?? "name", width: 10 }])} data-testid={`${testid}-add-col`}>
          Колонка
        </Button>
      )}
      <Typography.Text strong style={{ display: "block", margin: "10px 0 4px" }}>
        Итоги под таблицей
      </Typography.Text>
      {totals.map((total, i) => (
        <div key={i} className="wms-layout-row">
          <Select
            value={total.column}
            disabled={readOnly}
            options={block.columns.map((c) => ({ value: c.field, label: `под «${c.title}»` }))}
            onChange={(columnField: string) => setTotals(replaceAt(totals, i, { ...total, column: columnField }))}
            style={{ width: "100%" }}
          />
          <FieldSelect fields={headerFields} value={total.field} disabled={readOnly} onChange={(v) => setTotals(replaceAt(totals, i, { ...total, field: v ?? total.field }))} />
          <Select allowClear placeholder="Формат" value={total.format ?? undefined} disabled={readOnly} options={FORMAT_OPTIONS} onChange={(v) => setTotals(replaceAt(totals, i, { ...total, format: (v as LayoutFormat | undefined) ?? null }))} />
          <IconButtons index={i} count={totals.length} readOnly={readOnly} onMove={(to) => setTotals(moveItem(totals, i, to))} onRemove={() => setTotals(removeAt(totals, i))} testid={`${testid}-total-${i}`} />
        </div>
      ))}
      {!readOnly && totals.length < 8 && (
        <Button
          size="small"
          icon={<PlusOutlined />}
          onClick={() => setTotals([...totals, { column: block.columns[block.columns.length - 1]?.field ?? "", field: headerFields.find((f) => f.path.startsWith("totals."))?.path ?? headerFields[0]?.path ?? "" }])}
        >
          Итог
        </Button>
      )}
      <Row gutter={12} style={{ marginTop: 8 }}>
        <Labeled label="Подпись итога" span={12}>
          <Input value={block.total_label ?? "Итого"} disabled={readOnly} maxLength={60} onChange={(e) => onChange({ ...block, total_label: e.target.value })} />
        </Labeled>
        <Labeled label="Текст, когда строк нет" span={12}>
          <Input value={block.empty_text ?? "Строк нет"} disabled={readOnly} maxLength={200} onChange={(e) => onChange({ ...block, empty_text: e.target.value })} />
        </Labeled>
      </Row>
    </>
  );
}

/**
 * KZ-DOC UI02: the block editor of a declarative layout — page settings, blocks (add, move, remove), field pickers from
 * the catalogue of the document kind (`GET /document-sources`) and the requisites without which the layout is not
 * issued. A published version opens read-only (AT05).
 */
export function LayoutEditor({ value, onChange, source, readOnly }: Props) {
  const page = { ...DEFAULT_PAGE, ...(value.page ?? {}) };
  const setPage = (patch: Partial<typeof page>) => onChange({ ...value, page: { ...page, ...patch } });
  const blocks = value.blocks;
  const setBlocks = (next: LayoutBlock[]) => onChange({ ...value, blocks: next });
  const margins = page.margins_mm;
  const marginNames = ["Сверху", "Справа", "Снизу", "Слева"];
  return (
    <div data-testid="layout-editor">
      <Typography.Title level={5} style={{ marginTop: 0 }}>
        Страница
      </Typography.Title>
      <Row gutter={12}>
        <Labeled label="Формат" span={6}>
          <Select value={page.size} disabled={readOnly} onChange={(size) => setPage({ size })} options={[{ value: "A4", label: "A4" }, { value: "A5", label: "A5" }]} style={{ width: "100%" }} data-testid="layout-page-size" />
        </Labeled>
        <Labeled label="Ориентация" span={6}>
          <Select value={page.orientation} disabled={readOnly} onChange={(orientation) => setPage({ orientation })} options={[{ value: "portrait", label: "Книжная" }, { value: "landscape", label: "Альбомная" }]} style={{ width: "100%" }} data-testid="layout-page-orientation" />
        </Labeled>
        <Labeled label="Размер шрифта, пт" span={6}>
          <InputNumber min={7} max={12} step={0.5} value={page.font_size} disabled={readOnly} onChange={(v) => setPage({ font_size: Number(v ?? 9) })} style={{ width: "100%" }} />
        </Labeled>
        <Labeled label="Номера страниц" span={6}>
          <Switch checked={page.page_numbers} disabled={readOnly} onChange={(page_numbers) => setPage({ page_numbers })} />
        </Labeled>
        {marginNames.map((name, i) => (
          <Labeled key={name} label={`Поле ${name.toLowerCase()}, мм`} span={6}>
            <InputNumber
              min={0}
              max={60}
              value={margins[i]}
              disabled={readOnly}
              onChange={(v) => {
                const next = [...margins] as [number, number, number, number];
                next[i] = Number(v ?? 0);
                setPage({ margins_mm: next });
              }}
              style={{ width: "100%" }}
            />
          </Labeled>
        ))}
        <Labeled label="Нижний колонтитул (можно вставить поле)" span={24}>
          <TextWithFields value={page.footer ?? ""} onChange={(footer) => setPage({ footer: footer || null })} fields={source?.fields ?? []} readOnly={readOnly} maxLength={200} />
        </Labeled>
      </Row>

      <Typography.Title level={5}>Блоки макета · {blocks.length}</Typography.Title>
      <Space direction="vertical" style={{ width: "100%" }} size={10}>
        {blocks.map((block, i) => (
          <div key={i} className="wms-layout-block" data-testid={`layout-block-${i}`} data-type={block.type}>
            <div className="wms-layout-block-head">
              <span style={{ minWidth: 0 }}>
                <Tag bordered={false} color="blue">
                  №{i + 1} · {BLOCK_LABELS[block.type]}
                </Tag>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {blockSummary(block, source)}
                </Typography.Text>
              </span>
              <IconButtons index={i} count={blocks.length} readOnly={readOnly} onMove={(to) => setBlocks(moveItem(blocks, i, to))} onRemove={() => setBlocks(removeAt(blocks, i))} testid={`layout-block-${i}`} />
            </div>
            <BlockBody block={block} onChange={(b) => setBlocks(replaceAt(blocks, i, b))} source={source} readOnly={readOnly} testid={`layout-block-${i}`} />
          </div>
        ))}
      </Space>
      {!readOnly && (
        <div style={{ marginTop: 12 }}>
          <Typography.Text type="secondary" style={{ display: "block", marginBottom: 6 }}>
            Добавить блок в конец макета:
          </Typography.Text>
          <Space wrap size={[6, 6]}>
            {BLOCK_ORDER.map((type: LayoutBlockType) => {
              const reason = cannotAdd(type, source);
              const button = (
                <Button key={type} size="small" icon={<PlusOutlined />} disabled={!!reason || blocks.length >= 60} onClick={() => setBlocks([...blocks, defaultBlock(type, source)])} data-testid={`layout-add-${type}`}>
                  {BLOCK_LABELS[type]}
                </Button>
              );
              return reason ? (
                <Tooltip key={type} title={reason}>
                  <span>{button}</span>
                </Tooltip>
              ) : (
                button
              );
            })}
          </Space>
        </div>
      )}

      <Typography.Title level={5} style={{ marginTop: 18 }}>
        Обязательные реквизиты
      </Typography.Title>
      <Typography.Paragraph type="secondary" style={{ marginBottom: 6 }}>
        Без этих данных документ по макету не выдаётся: просмотр покажет, что заполнить и где. Значения не подставляются по догадке.
      </Typography.Paragraph>
      <Select
        mode="multiple"
        value={value.required ?? []}
        disabled={readOnly}
        onChange={(required: string[]) => onChange({ ...value, required })}
        options={fieldOptions(source?.fields ?? [])}
        filterOption={(input, option) => (option?.search ?? "").includes(input.toLowerCase())}
        style={{ width: "100%" }}
        placeholder="Например: Наша организация: БИН/ИИН"
        data-testid="layout-required"
      />
    </div>
  );
}
